/**
 * Lanes: one Serving switch per lane. Off removes the lane from every policy,
 * pin and rescue hop within a minute. One tap, no reason required: this is
 * the outage switch.
 */

import { useState } from "react";
import { Switch } from "../../../ui/switch.js";
import type { Lane, LaneState } from "./types.js";
import { LANE_NAMES } from "./format.js";
import { JSON_HEADERS, runMutation } from "./http.js";
import { PRINT_CARD_CLASS, PRINT_CARD_STYLE } from "./styles.js";
import { LaneBadges } from "./LaneBadges.js";

interface LanesCardProps {
  lanes: LaneState[];
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}

export function LanesCard({ lanes, onChanged, onError }: LanesCardProps) {
  const [toggling, setToggling] = useState<Lane | null>(null);

  async function setServing(lane: Lane, serving: boolean) {
    setToggling(lane);
    await runMutation(
      () =>
        fetch(`/api/admin/tier-models/lanes/${encodeURIComponent(lane)}`, {
          method: "PUT",
          headers: JSON_HEADERS,
          body: JSON.stringify({ serving }),
        }),
      `Unable to switch ${LANE_NAMES[lane]}`,
      { onError, onSuccess: onChanged },
    );
    setToggling(null);
  }

  return (
    <section aria-label="Lanes" className={PRINT_CARD_CLASS} style={PRINT_CARD_STYLE}>
      <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
        Lanes
      </h3>
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        Turn a lane off when it is down. Off removes it from every policy, pin and rescue hop
        within a minute. Health is in Model health below.
      </p>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {lanes.map((lane) => (
          <div
            key={lane.lane}
            className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] border px-3 py-2"
            style={{
              borderColor: "var(--border-subtle)",
              background: lane.serving ? "rgba(var(--glass-rgb), .6)" : "rgba(var(--ink-rgb), .04)",
            }}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>
                {LANE_NAMES[lane.lane]}
                <LaneBadges lane={lane.lane} />
              </div>
              <div className="text-[11px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                {!lane.keyConfigured ? "Not serving · key missing" : lane.serving ? "Serving" : "Off"}
              </div>
            </div>
            <Switch
              aria-label={`${LANE_NAMES[lane.lane]} serving`}
              checked={lane.serving}
              disabled={toggling !== null || !lane.keyConfigured}
              onCheckedChange={(checked) => void setServing(lane.lane, Boolean(checked))}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
